import React, { useMemo } from "react";
import { Grid, Typography, Alert, AlertTitle, Button } from "@mui/material";
import { format, isToday } from "date-fns";
import { CLIENT_STATUS } from "../../../../constants";
import FieldItem from "../../Widgets/FieldItem";
import { ClientAccountSettings } from "./interface";

interface ViewFieldsProps {
  accountSettings: ClientAccountSettings;
  onEditFutureDeactivation?: () => void;
  onConfirmScheduledDeactivation?: () => void;
  canDeactivate?: boolean;
}

export const ViewFields: React.FC<ViewFieldsProps> = ({
  accountSettings,
  onEditFutureDeactivation,
  onConfirmScheduledDeactivation,
  canDeactivate,
}) => {
  const { workflowStatus, isActive, scheduledDeactivationData } = accountSettings;

  // Function to get the display text for the current status
  const getStatusDisplayText = (statusKey: string) => CLIENT_STATUS[statusKey as keyof typeof CLIENT_STATUS] || "-";

  // Format the scheduled deactivation date in human-readable format
  const formattedDeactivationDate = useMemo(() => {
    if (!scheduledDeactivationData?.scheduled_deactivation_date) return null;
    try {
      const dateStr = scheduledDeactivationData.scheduled_deactivation_date;
      const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
      const dateObj = new Date(year, month - 1, day);
      return format(dateObj, "MMMM dd, yyyy");
    } catch {
      return null;
    }
  }, [scheduledDeactivationData?.scheduled_deactivation_date]);

  // Check if deactivation date is today
  const isDeactivationToday = useMemo(() => {
    if (!scheduledDeactivationData?.scheduled_deactivation_date) return false;
    try {
      const dateStr = scheduledDeactivationData.scheduled_deactivation_date;
      const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
      const dateObj = new Date(year, month - 1, day);
      return isToday(dateObj);
    } catch {
      return false;
    }
  }, [scheduledDeactivationData?.scheduled_deactivation_date]);

  const deactivationStatus = scheduledDeactivationData?.deactivation_status;

  return (
    <Grid container sx={{ rowGap: "24px" }}>
      <Grid item xs={12}>
        <FieldItem
          label="Account status"
          value={getStatusDisplayText(workflowStatus)}
        />
      </Grid>

      {/* Alert for scheduled future deactivation - visible all the time */}
      {deactivationStatus === "processing" && formattedDeactivationDate && (
        <Grid item xs={12}>
          <Alert
            severity="warning"
            action={
              canDeactivate && (
                <Button
                  style={{ backgroundColor: "transparent", color: "inherit" }}
                  variant="text"
                  size="small"
                  onClick={
                    isDeactivationToday
                      ? onConfirmScheduledDeactivation
                      : onEditFutureDeactivation
                  }
                >
                  {isDeactivationToday ? "Confirm Deactivation" : "Edit Deactivation"}
                </Button>
              )
            }
          >
            <AlertTitle>{`Deactivation set for ${formattedDeactivationDate}`}</AlertTitle>
            Confirmation is required prior to deactivation
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <FieldItem
          label="Client active"
          value={isActive ? "Yes" : "No"}
        />
      </Grid>
    </Grid>
  );
};

export default ViewFields;
